export const DOWNLOAD_URL = "https://github.com/PbL34D/L34D-Packages/releases#release-L34D-Fighter-Foundry-v0.0.1";
export const REPO_URL = 'https://github.com/PbL34D/L34D-Fighter-Foundry';
export const MEMBER_DOWNLOAD_URL = `https://auth.l34d.ca/api/applications?mode=login&return_to=${encodeURIComponent(DOWNLOAD_URL)}`;

export type FoundryPage = {
  slug: string;
  number: string;
  eyebrow: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  facts: [string, string][];
  sections: {
    title: string;
    body: string;
    bullets?: string[];
    code?: string;
    note?: string;
  }[];
};

export const frameRows = [
  { name: 'Candy', compiled: 312, active: 307 },
  { name: 'Vandal', compiled: 309, active: 218 },
  { name: 'Absolution', compiled: 358, active: 338 },
  { name: 'DJ', compiled: 394, active: 374 },
  { name: 'Beastly', compiled: 401, active: 381 },
  { name: 'PB', compiled: 354, active: 299 },
  { name: 'Shiv', compiled: 392, active: 372 },
];

export const foundryPages: FoundryPage[] = [
  {
    slug: 'pipeline',
    number: '01',
    eyebrow: 'Source to runtime',
    title: 'The asset production pipeline',
    summary: 'A traceable path from approved reference material to normalized frames, engine assets, browser previews and a verified release.',
    image: '/assets/foundry/pipeline-workbench.png',
    imageAlt: 'An illustrated sprite production workbench showing references, frame cleanup, collision data and a running build',
    facts: [['5', 'named production gates'], ['3', 'runtime targets'], ['1', 'source owner'], ['0', 'manual release copies']],
    sections: [
      {
        title: 'Define the contract before drawing',
        body: 'Every fighter begins with an explicit target: local coordinate system, facing direction, frame envelope, action inventory and output locations. The contract lets builders reject malformed work before it reaches a character definition.',
        bullets: ['Reference intake stays separate from generated runtime assets', 'Character-specific policy records legal heights and facing rules', 'Every output path has one builder responsible for it'],
        code: `{
  "localcoord": "1280,720",
  "facing": "right",
  "minimumHeight": 540,
  "maximumHeight": 720
}`,
      },
      {
        title: 'Assemble once, publish to several consumers',
        body: 'The same approved transparent frames feed the Ikemen SFF/AIR build, browser idle previews, contact sheets and release evidence. The pipeline does not hand-edit a second copy for the website.',
        bullets: ['PNG32 source frames preserve alpha', 'AIR actions and collision boxes are generated deterministically', 'Web previews are optimized derivatives, never the source of truth'],
        code: `node .\\The-Smackers-Game\\tools\\build-assets.mjs
node .\\The-Smackers-Game\\tools\\validate-project.mjs
npm run build && npm run verify`,
      },
      {
        title: 'Verification is a production stage',
        body: 'Compilation is necessary but not sufficient. The release gate checks inventories, required files, animation reachability and visual review sheets so a successful build cannot quietly omit a performer.',
        note: 'A failed gate remains a failed release even when the executable launches.',
      },
    ],
  },
  {
    slug: 'character-harness',
    number: '02',
    eyebrow: 'Character engineering',
    title: 'The fighter harness',
    summary: 'A repeatable character scaffold for turning hundreds of transparent drawings into actions, state logic, hit geometry, audio and an inspectable roster entry.',
    image: '/assets/characters/absolution/action-960.webp',
    imageAlt: 'Absolution action artwork used by the Fighter Foundry',
    facts: [['15', 'Windows prototypes'], ['79', 'largest action set'], ['401', 'largest compiled frame set'], ['1280×720', 'authoring coordinates']],
    sections: [
      {
        title: 'One definition joins every character owner',
        body: 'The DEF file is deliberately plain. It names the command map, constants, state controllers, sprite archive, AIR animation, sound bank and movelist. Builders can replace generated assets without changing the contract.',
        code: `[Info]
localcoord = 1280,720

[Files]
cmd = wake.cmd
cns = wake.cns
st = wake.st
sprite = wake.sff
anim = wake.air
sound = wake.snd
movelist = movelist.dat`,
      },
      {
        title: 'Actions are data, not folder folklore',
        body: 'A frame is only production-ready when it is reachable from an AIR action and that action is reachable from character state. This distinction explains why compiled and active counts differ.',
        bullets: ['Idle, walk, guard, hit and attack actions have explicit IDs', 'Collision boxes are authored or generated per action family', 'Unreachable drawings remain visible in the audit instead of disappearing'],
      },
      {
        title: 'The harness scales without erasing personality',
        body: 'The scaffold standardizes mechanics while each fighter keeps distinct silhouettes, pacing, voice, stage association and special-state logic. It reduces integration work; it does not flatten character design.',
      },
    ],
  },
  {
    slug: 'ikemen-integration',
    number: '03',
    eyebrow: 'Runtime adaptation',
    title: 'Ikemen GO integration',
    summary: 'How character packages, stage layers and game data are adapted for a deterministic 1280×720 fighting runtime without coupling the source-art workspace to engine internals.',
    image: '/assets/foundry/ikemen-integration-lab.png',
    imageAlt: 'An illustrated Ikemen integration lab with layered stage art and character data panels',
    facts: [['1280×720', 'local coordinate target'], ['74', 'Absolution actions'], ['66', 'Shiv actions'], ['1', 'packaged Windows runtime']],
    sections: [
      {
        title: 'Character packages remain portable',
        body: 'Each fighter folder contains the engine-facing contract. Relative file references make the package inspectable and movable, while builders retain ownership of generated SFF and AIR artifacts.',
        bullets: ['CMD owns input grammar', 'CNS and ST own constants and state controllers', 'SFF/AIR/SND own generated media and timing'],
      },
      {
        title: 'Stages are layered systems',
        body: 'Candy Island of Corn is authored as a stage definition, not a flattened screenshot. Camera bounds, parallax deltas, foreground layers and destinations are independently tunable.',
        code: `[StageInfo]
localcoord = 1280,720

[BG candy-sky]
type = parallax
delta = 0.35, 0.35

[BG pit-edge]
type = normal
layerno = 1`,
      },
      {
        title: 'Adaptation happens at the boundary',
        body: 'Source art can evolve at high fidelity while engine outputs stay constrained by memory, timing and coordinate requirements. Conversion scripts are the boundary, which makes engine upgrades and asset rebuilds reviewable.',
        note: 'The checked-in Windows runtime is the integration test target, not the authoring source.',
      },
    ],
  },
  {
    slug: 'visual-qa',
    number: '04',
    eyebrow: 'Evidence before release',
    title: 'Visual QA and deterministic checks',
    summary: 'Contact sheets, reachability counts and required-asset gates make omissions visible before a package is promoted to testers.',
    image: '/assets/foundry/qa-release-bench.png',
    imageAlt: 'An illustrated quality assurance station comparing sprite sheets, logs and a running game build',
    facts: [['2,520', 'compiled frames sampled'], ['2,289', 'active frames sampled'], ['8', 'published contact sheets'], ['1', 'repeatable verification command']],
    sections: [
      {
        title: 'Contact sheets expose sequence defects',
        body: 'A contact sheet compresses a full idle or action sequence into one review surface. Cropping, inconsistent scale, reversed facing, alpha halos and sudden pose discontinuities become obvious without scrubbing folders.',
        bullets: ['Generated from the same frame inventory used by the build', 'Stable ordering makes before/after review practical', 'The sheet is evidence, not a replacement asset'],
      },
      {
        title: 'Reachability is measured explicitly',
        body: 'Compiled count reports drawings accepted by the asset builder. Active count reports drawings reachable through current animation and state data. The gap is a useful diagnostic, not a vanity score.',
        code: `compiledFrames = sourceFrames.filter(validateFrame)
activeFrames = airActions
  .flatMap(action => action.frames)
  .filter(frame => compiledFrames.has(frame.id))`,
      },
      {
        title: 'The validator names missing production owners',
        body: 'The project validator checks required paths, generated assets, game data and release surfaces. Failure output identifies the responsible file or gate so errors do not get buried in a successful package command.',
      },
    ],
  },
  {
    slug: 'smackers-progress',
    number: '05',
    eyebrow: 'Current build',
    title: 'The Smackers progress ledger',
    summary: 'A factual view of the current playable surfaces, roster throughput and remaining production risk—without pretending alpha work is finished.',
    image: '/assets/fighting/stages/candy-island-of-corn.webp',
    imageAlt: 'Candy Island of Corn stage used by The Smackers',
    facts: [['31', 'browser fighters and bosses'], ['465', 'versus pairings'], ['20', 'finale levels'], ['2', 'browser modes']],
    sections: [
      {
        title: 'Playable now',
        body: 'The public cabinet includes an attract campaign, roster selection, browser fighting demonstration, finale shooter and ranked champion records. The private Windows workspace carries the deeper Ikemen integration.',
        bullets: ['31 browser fighters and final bosses', '465 unique pairings across the current roster', '20 authored finale combat levels', 'Two playable browser modes'],
      },
      {
        title: 'Production depth is uneven by design',
        body: 'Characters progress independently. Some have broad active action coverage; others still carry compiled drawings that are not reachable from the current game data. The frame ledger below exposes that work honestly.',
      },
      {
        title: 'Next milestones',
        body: 'The next pass focuses on action reachability, collision review, stage behavior and repeatable packaging. Content growth follows stable harness behavior rather than outrunning it.',
        bullets: ['Close the largest compiled-versus-active frame gaps', 'Expand real match-state testing', 'Keep browser proof synchronized with private runtime changes'],
      },
    ],
  },
  {
    slug: 'release-access',
    number: '06',
    eyebrow: 'Build and distribution',
    title: 'Release access and tester builds',
    summary: 'Where the verified executable comes from, how app-qualified releases are addressed, and why the visible tester link still requires accepted team membership.',
    image: '/assets/characters/wake/action-960.webp',
    imageAlt: 'Wake action artwork used by the Fighter Foundry',
    facts: [['0.0.1', 'current Foundry release'], ['1', 'qualified package anchor'], ['Private', 'source repository'], ['Members', 'tester access']],
    sections: [
      {
        title: 'The executable has one integration location',
        body: 'The test runtime is built and exercised from The-Smackers-Game/SmackdownSupreme.exe. Release tooling packages that current integration output instead of maintaining a second hand-copied executable.',
        code: `The-Smackers-Game/
├─ SmackdownSupreme.exe
├─ data/
├─ chars/
├─ stages/
└─ tools/`,
      },
      {
        title: 'Every website targets the app-qualified release',
        body: 'Fighter Foundry links to its exact L34D-Packages release anchor. When DevControl changes the version, it rewrites this source constant and displays a required website-publish reminder.',
        note: DOWNLOAD_URL,
      },
      {
        title: 'Visibility does not bypass authorization',
        body: 'The tester action is intentionally visible, but it enters the L34D sign-in flow and ultimately relies on GitHub access to the private package. Only accepted collaborators or team members can retrieve the build.',
      },
    ],
  },
];

export function pageBySlug(slug: string) {
  return foundryPages.find(page => page.slug === slug);
}
